import React from "react";
import { useTranslation } from "react-i18next";
import { useReactiveVar } from "@apollo/client";
import { Mail, MessageCircle, UsersRound } from "lucide-react";
import { DataCell } from "@/components/DataCell";
import { useLiveEventDetailStats } from "@/hooks/useLiveEventDetailStats";
import { filterStateVar } from "@/reactiveVars/organization";
import { CircleIcon } from "@/components/DashboardOverviewDataCells/CircleIcon";

interface ResultDataCardsProps {
  eventId: string;
}

export const ResultDataCards = ({ eventId }: ResultDataCardsProps) => {
  const { t } = useTranslation();
  const filterState = useReactiveVar(filterStateVar);

  const { statistics, loading, error } = useLiveEventDetailStats(
    eventId,
    filterState,
  );

  const formatCount = (count: number | null | undefined): string => {
    if (count === null || count === undefined) {
      return "--";
    }
    return count.toLocaleString();
  };

  const hasError = Boolean(error);
  const isLoading = loading && !hasError;

  const errorContent = (
    <span className="text-sm text-muted-foreground">
      {t("home.overview.dataCells.error.loadingError")}
    </span>
  );

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
      {/* Total Participants */}
      <DataCell
        className="h-28"
        isLoading={isLoading}
        title={t("eventDetails.results.bentos.totalParticipants")}
        middleValue={
          hasError ? errorContent : formatCount(statistics?.totalParticipants)
        }
        icon={
          <CircleIcon color="emerald">
            <UsersRound />
          </CircleIcon>
        }
      />

      {/* Total Channel Opens */}
      <DataCell
        className="h-28"
        isLoading={isLoading}
        title={t("eventDetails.results.bentos.totalChannelOpens")}
        middleValue={
          hasError ? errorContent : formatCount(statistics?.totalChannelOpens)
        }
        icon={
          <CircleIcon color="indigo">
            <MessageCircle />
          </CircleIcon>
        }
      />

      {/* Total Messages */}
      <DataCell
        className="h-28"
        isLoading={isLoading}
        title={t("eventDetails.results.bentos.totalMessages")}
        middleValue={
          hasError ? errorContent : formatCount(statistics?.totalMessages)
        }
        icon={
          <CircleIcon color="yellow">
            <Mail />
          </CircleIcon>
        }
      />

      {/* Students who Messaged */}
      <DataCell
        className="h-28"
        isLoading={isLoading}
        title={t("eventDetails.results.bentos.studentsWhoMessaged")}
        middleValue={
          hasError ? errorContent : formatCount(statistics?.studentsWhoMessaged)
        }
        icon={
          <CircleIcon color="orange">
            <MessageCircle />
          </CircleIcon>
        }
      />
    </div>
  );
};
